import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    role: "entrepreneur" | "mentor",
    options?: { extraMetadata?: Record<string, unknown>; emailRedirectTo?: string },
  ) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // If this is a password recovery event, redirect to reset password page
        if (event === 'PASSWORD_RECOVERY') {
          window.location.href = '/reset-password';
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    role: "entrepreneur" | "mentor",
    options?: { extraMetadata?: Record<string, unknown>; emailRedirectTo?: string },
  ) => {
    // Block reserved/impersonation domains
    const blockedDomains = ['@eabconnect.com'];
    const emailLower = email.toLowerCase();
    
    for (const domain of blockedDomains) {
      if (emailLower.endsWith(domain)) {
        return { 
          error: new Error('Registration from this email domain is not allowed. Please use a different email address.') 
        };
      }
    }
    
    // Always use production domain for email verification links
    const redirectUrl = options?.emailRedirectTo ?? "https://eabconnect.com/";
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          role: role,
          ...options?.extraMetadata,
        },
      },
    });

    // Check for the "fake success" case - email already exists in auth.identities
    // This happens when a user was deleted but orphaned identity records remain,
    // or when the user simply hasn't confirmed their email yet
    if (!error && data.user && data.user.identities?.length === 0) {
      console.log("Detected existing email, attempting to resend confirmation");
      console.log("User data:", { userId: data.user.id, email: data.user.email, identities: data.user.identities });
      
      // Try to resend the confirmation email
      const { data: resendData, error: resendError } = await supabase.auth.resend({
        email,
        type: 'signup',
      });
      
      console.log("Resend confirmation result:", { resendData, resendError });
      
      if (resendError) {
        console.error("Failed to resend confirmation:", resendError.message, resendError);
        // If resend fails, include that info in the error message
        return { 
          error: new Error(`This email is already registered but we couldn't resend the confirmation email (${resendError.message}). Please try signing in or use "Forgot Password".`) 
        };
      }
      
      console.log("Confirmation email resend successful");
      
      // Return a helpful error message
      return { 
        error: new Error("This email is already registered. We've sent a new confirmation email - please check your inbox and spam folder. If you already have an account, try signing in instead.") 
      };
    }

    // If signup was successful, notify admins
    if (!error && data.user) {
      try {
        await supabase.functions.invoke("notify-admin-new-profile", {
          body: {
            user_id: data.user.id,
            full_name: fullName,
            email: email,
            role: role,
          },
        });
      } catch (notifyError) {
        console.error("Failed to notify admins:", notifyError);
        // Don't fail the signup if notification fails
      }
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    // Always use production domain for password reset links
    const redirectUrl = "https://eabconnect.com/reset-password";
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
